/*
* Free gift with purchase
*
* Adds the gift product (snippets/free-gift.liquid config) to the cart once the
* cart total after discounts, excluding the gift, reaches the threshold, and
* removes it when the total drops below. The price is set to $0 by a separate
* automatic discount in Shopify admin; this file only manages the cart line and
* the progress message.
*
* Hooks into Broadcast's cart: listens for `theme:cart:change` (fired after every
* cart drawer / cart page rebuild) and re-renders with `window.cart.getCart()`.
*/

(function() {
  const selectors = {
    config: '[data-free-gift-config]',
    progress: '[data-free-gift-progress]',
    amount: '[data-free-gift-amount]',
    bar: '[data-free-gift-bar]',
  };

  const classes = {
    success: 'is-success',
    hidden: 'is-hidden',
  };

  const GIFT_PROPERTY = '_free_gift';
  const FAILED_KEY = 'free_gift_add_failed';
  const FAILED_RETRY_AFTER = 30 * 60 * 1000; // Don't retry a failed add (e.g. sold out) for 30 minutes
  const MAX_SELF_UPDATES = 3; // Loop guard for consecutive automatic cart changes

  const configElement = document.querySelector(selectors.config);
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }

  if (!config.variantId) return;

  // The gift discount is 100% off the gift product with no minimum, so this file
  // is the only gatekeeper: every line of the gift product counts as the gift.

  const routes = window.theme.routes;
  const rate = window.Shopify && window.Shopify.currency && Number(window.Shopify.currency.rate) ? Number(window.Shopify.currency.rate) : 1;
  const threshold = Math.round(config.threshold * rate);

  // Match the free shipping message: add the active currency code (CAD, USD...) when the theme shows codes
  const currencyCode = window.theme.moneyFormat === window.theme.moneyWithCurrencyFormat && window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : '';

  let isRunning = false;
  let runAgain = false;
  let selfUpdates = 0;
  let suppressDrawerOpen = false;

  // The theme opens the cart drawer after every rebuild (`theme:product:added`).
  // Stop that when we refresh the cart ourselves while the drawer is closed.
  window.addEventListener(
    'theme:product:added',
    (event) => {
      if (suppressDrawerOpen) {
        suppressDrawerOpen = false;
        event.stopImmediatePropagation();
      }
    },
    true
  );

  document.addEventListener('theme:cart:change', () => sync());

  sync();

  /**
   * Bring the gift line in line with the cart total, then update the message
   */
  async function sync() {
    if (isRunning) {
      runAgain = true;
      return;
    }

    isRunning = true;

    try {
      let cart = await getCart();
      const changed = await updateGiftLine(cart);

      if (changed) {
        cart = await getCart();
        selfUpdates += 1;
        updateProgress(cart);

        if (selfUpdates <= MAX_SELF_UPDATES) {
          refreshThemeCart(cart);
        }
      } else {
        selfUpdates = 0;
        updateProgress(cart);
      }
    } catch (error) {
      console.log(error);
    } finally {
      isRunning = false;

      if (runAgain) {
        runAgain = false;
        sync();
      }
    }
  }

  /**
   * Add, remove or fix the quantity of the gift line
   *
   * @return  {Boolean}  whether the cart was changed
   */
  async function updateGiftLine(cart) {
    const giftItems = cart.items.filter(isGiftItem);
    const eligibleTotal = getEligibleTotal(cart);

    if (eligibleTotal >= threshold) {
      if (giftItems.length === 0) {
        if (!config.available || hasRecentlyFailed()) return false;

        const added = await addGift();
        if (!added) setFailed();

        return added;
      }

      // Keep a single gift unit in total, preferring the line added by this script
      const keepItem = giftItems.find((item) => item.properties && item.properties[GIFT_PROPERTY]) || giftItems[0];
      let changed = false;
      for (const item of giftItems) {
        const quantity = item === keepItem ? 1 : 0;
        if (item.quantity !== quantity) {
          await changeLine(item.key, quantity);
          changed = true;
        }
      }

      return changed;
    }

    if (giftItems.length === 0) return false;

    for (const item of giftItems) {
      await changeLine(item.key, 0);
    }

    return true;
  }

  function isGiftItem(item) {
    return item.variant_id === config.variantId || (config.productId && item.product_id === config.productId) || Boolean(item.properties && item.properties[GIFT_PROPERTY]);
  }

  function getEligibleTotal(cart) {
    return cart.items.filter(isGiftItem).reduce((total, item) => total - item.final_line_price, cart.total_price);
  }

  function getCart() {
    return fetch(`${routes.cart_url}.js`, {
      headers: {Accept: 'application/json'},
    }).then((response) => response.json());
  }

  function addGift() {
    const properties = {};
    properties[GIFT_PROPERTY] = 'true';

    return fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({
        items: [
          {
            id: config.variantId,
            quantity: 1,
            properties: properties,
          },
        ],
      }),
    })
      .then((response) => response.ok)
      .catch(() => false);
  }

  function changeLine(key, quantity) {
    return fetch(`${routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({id: key, quantity: quantity}),
    });
  }

  function hasRecentlyFailed() {
    try {
      const failedAt = Number(window.sessionStorage.getItem(FAILED_KEY));
      return failedAt && Date.now() - failedAt < FAILED_RETRY_AFTER;
    } catch (error) {
      return false;
    }
  }

  function setFailed() {
    try {
      window.sessionStorage.setItem(FAILED_KEY, Date.now());
    } catch (error) {
      // Ignore storage errors
    }
  }

  /**
   * Re-render the cart drawer / cart page through the theme's own flow
   */
  function refreshThemeCart(cart) {
    const themeCart = window.cart;

    if (themeCart && themeCart.cart) {
      suppressDrawerOpen = !themeCart.cartDrawerIsOpen;
      themeCart.getCart();
      return;
    }

    // No cart drawer on this page: only update the header cart count
    document.dispatchEvent(
      new CustomEvent('theme:cart:change', {
        bubbles: true,
        detail: {cartCount: cart.item_count},
      })
    );
  }

  /**
   * Update the progress message in the cart drawer and on the cart page
   */
  function updateProgress(cart) {
    const eligibleTotal = getEligibleTotal(cart);
    const hasGift = cart.items.some(isGiftItem);
    const isSuccess = eligibleTotal >= threshold;
    const percent = Math.max(0, Math.min(100, (eligibleTotal / threshold) * 100));
    const amount = formatMoney(Math.max(0, threshold - eligibleTotal));
    const isHidden = cart.item_count === 0 || (isSuccess && !hasGift) || (!isSuccess && !config.available);

    document.querySelectorAll(selectors.progress).forEach((progress) => {
      const amountHolder = progress.querySelector(selectors.amount);
      const bar = progress.querySelector(selectors.bar);

      if (amountHolder) amountHolder.textContent = amount;
      if (bar) bar.value = percent;

      progress.classList.toggle(classes.success, isSuccess);
      progress.classList.toggle(classes.hidden, isHidden);
    });
  }

  function formatMoney(cents) {
    const format = window.theme.moneyWithoutCurrencyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const withDelimiters = (number, thousands, decimal) => {
      const parts = number.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts.join(decimal);
    };

    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withDelimiters(Math.round(cents / 100).toString(), ',', '.'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withDelimiters(value, '.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, withDelimiters(value, ',', '.'))
      .replace(/[.,]00(?!\d)/, '')
      .concat(currencyCode ? ` ${currencyCode}` : '');
  }
})();
